/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty, typeOf } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import DS from 'ember-data';
import { later } from '@ember/runloop';
import CustomerModel from 'cio/models/customer';
import ApiService from 'cio/services/api';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import { sanitize } from 'cio/utils/sanitize';
import { config } from 'cio/helpers/preview-whitelist';
import FormValidations from 'cio/utils/form-validations';
import {
  MAX_EVENT_NAME_SIZE,
  MAX_EVENT_DATA_SIZE,
  DISALLOWED_EVENT_DATA_PROPERTIES,
  CUSTOMERS_LIMIT,
} from 'cio/utils/event-constants';
import { seriesString } from 'cio/utils/formatters';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import EnvironmentService from 'cio/services/environment';
import { IdentifierKey } from 'cio/utils/identifiers';
import { isFacebookForm } from 'cio/utils/forms';
import CustomForm from 'cio/models/custom-form';
import cioConfig from 'cio/config/environment';

interface SendEventModalArgs {
  selectedCustomer?: CustomerModel;
  eventName?: string;
  eventData?: string;
  form?: FacebookLeadAdsForm | CustomForm;
  source?: string;
  readOnly?: boolean;
  onClose: () => unknown;
  onSend?: () => unknown;
}

type Event = {
  customer?: CustomerModel;
  name?: string;
  data?: string;
};

enum EventEditorType {
  Simple = 'Simple',
  JSON = 'JSON',
  FORMS = 'Forms',
}

const EventValidations = {
  customer(value: CustomerModel) {
    if (!value) {
      return 'Please select a Person';
    }

    return true;
  },

  name(value: string) {
    if (!value || isEmpty(value)) {
      return 'Please enter the event name to send';
    }

    if (value.length > MAX_EVENT_NAME_SIZE) {
      return 'Event name must not be more than 100 characters';
    }

    return true;
  },

  data(this: SendEventModal, value: string | undefined) {
    if (!value) {
      return true;
    }

    try {
      let json = JSON.parse(value);

      // The typeOf TS definitions are broken (they don't seem to recognize
      // 'object'), so we need to cast to "any"
      let jsonType = typeOf(json) as any;
      if (jsonType !== 'object' && jsonType !== 'array') {
        let error = 'Event data must be valid JSON object or array';

        this.trackEventError(error);

        return error;
      }

      if (value.length > MAX_EVENT_DATA_SIZE) {
        let error = 'Event data must not exceed 10 MB in size';

        this.trackEventError(error);

        return error;
      }

      if (Object.keys(json).filter((key) => DISALLOWED_EVENT_DATA_PROPERTIES.includes(key)).length) {
        let error = `Event data cannot have properties named ${seriesString(
          DISALLOWED_EVENT_DATA_PROPERTIES.join(','),
          undefined,
          undefined,
          undefined,
          'or',
        )}`;

        this.trackEventError(error);

        return error;
      }

      return true;
    } catch {
      let error = 'Event data must be valid JSON';

      this.trackEventError(error);

      return error;
    }
  },
};

function sanitizeValue(value: any): any {
  if (value == null) {
    return value;
  }

  let valueType = typeOf(value);

  // @ts-ignore typeOf doesn't have "object" typed as a return type
  if (valueType === 'object') {
    return sanitizeObject(value);
  } else if (valueType === 'array') {
    return sanitizeArray(value);
  } else if (valueType === 'string') {
    return sanitizeString(value);
  }

  return value;
}

function sanitizeString(value: string): string {
  return sanitize(value, config);
}

function sanitizeArray(arr: any[]): any[] {
  return arr.map((value) => sanitizeValue(value));
}

function sanitizeObject(obj: Record<any, any>): Record<any, any> {
  return Object.keys(obj).reduce<typeof obj>((acc, key) => {
    acc[key] = sanitizeValue(obj[key]);
    return acc;
  }, {});
}

function sanitizeRawData(data: string | null | undefined): string {
  if (data == null) {
    return '';
  }

  try {
    let parsedData = JSON.parse(data);
    let sanitizedData = sanitizeValue(parsedData);
    return JSON.stringify(sanitizedData);
  } catch {
    return '';
  }
}

export default class SendEventModal extends Component<SendEventModalArgs> {
  readonly EDITOR_TYPES = EventEditorType;
  readonly EDITOR_TYPES_TO_CHOOSE = [EventEditorType.Simple, EventEditorType.JSON];

  validations: FormValidations;

  @service store!: DS.Store;
  @service api!: ApiService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;

  @tracked formValuesForForm: Record<string, string> = {};
  @tracked customers: CustomerModel[] = [];
  @tracked event: Event = {};

  @tracked editorType = EventEditorType.Simple;

  constructor(owner: unknown, args: SendEventModalArgs) {
    super(owner, args);

    let event: Event = {
      name: args.eventName ?? '',
      customer: args.selectedCustomer,
    };

    if (args.form) {
      this.editorType = EventEditorType.FORMS;
    } else if (args.eventData) {
      try {
        let json = JSON.parse(args.eventData);

        if (Object.keys(json).length) {
          event.data = args.eventData;
          this.editorType = EventEditorType.JSON;
        }
      } catch {}
    }

    this.event = event;

    this.validations = FormValidations.create({
      validations: EventValidations,
      context: this,
      modelKey: 'event',
    });

    if (this.args.form && isFacebookForm(this.args.form)) {
      this.formValuesForForm = this.args.form.attrMaps.reduce(
        (aggr, m) => ({
          ...aggr,
          [m.facebookQuestionKey.toLowerCase()]: '',
        }),
        { lead_id: '' },
      );
    }
  }

  // minor validations to avoid forcing the user to click "send" to see basic errors
  @computed('args.readOnly', 'event.{customer,name}')
  get sendDisabledHint() {
    if (this.args.readOnly ?? false) {
      return 'Contact an Admin to access this feature';
    }

    if (this.event.customer == null) {
      return 'Please select a person';
    }

    if (isEmpty(this.event.name)) {
      return 'Please enter the event name to send';
    }

    return null;
  }

  @action
  selectCustomer(customer: CustomerModel) {
    this.clearSearch();
    this.event = { ...this.event, customer };
    this.validations.validateAttribute('customer');
  }

  @action
  clearSearch() {
    this.customers = [];
    this.event = { ...this.event, customer: undefined };
    this.searchTask.cancelAll({ resetState: true });
  }

  @action
  switchEditor(editor: EventEditorType) {
    this.editorType = editor;
  }

  @action
  trackEventStarted() {
    this.analytics.track('events', 'send_started', { source: this.args.source, isResend: this.isResend });
  }

  @action
  trackEventError(message: string) {
    let { name, data: rawData } = this.event;

    this.analytics.track('events', 'error_encountered', {
      source: this.args.source,
      isResend: this.isResend,
      message,
      eventName: name,
      eventData: rawData,
      editorType: this.editorType,
    });
  }

  @action
  onNameChange(name: string) {
    this.event = {
      ...this.event,
      name,
    };
  }

  searchTask = dropTask(async (opts: { email: string } | { filters: string | null }) => {
    let customers = await this.store.query('customer', opts);
    this.customers = customers.toArray().slice(0, CUSTOMERS_LIMIT);
  });

  sendEventTask = dropTask(async () => {
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` errored out and already set validation errors
      // return early without actually doing anything:
      return;
    }

    let { customer, name, data: rawData } = this.event;
    let data = sanitizeRawData(rawData);

    try {
      await this.api.post('events', {
        data: {
          ids: [customer!.get('identifiers')[IdentifierKey.CioId]],
          name,
          data,
        },
      });

      let customerEmail = customer?.get('email');
      this.alerts.success(
        'event.success',
        `${name} event${customerEmail ? ` for ${customer?.get('email')}` : ''} sent!`,
      );

      let { source } = this.args;
      if (this.args.form) {
        source = isFacebookForm(this.args.form) ? 'Facebook Lead Ads' : 'Custom Forms';
      }

      this.analytics.track('events', 'send_completed', {
        eventName: name,
        eventData: data,
        editorType: this.editorType,
        isResend: this.isResend,
        source,
      });

      // update the store with the new event from the back end, but give it time to process for the
      // ~90th percentile of events
      later(
        this,
        () => {
          if (this.args.onSend) {
            this.args.onSend();
          }
        },
        cioConfig.APP.sendEventReloadDelay,
      );

      this.args.onClose();
    } catch {}
  });

  private get isResend() {
    return !isEmpty(this.args.eventName) && !isEmpty(this.args.selectedCustomer);
  }
}
